/**
 * VideoForm.jsx
 * The Input side of the Video screen: every P-Video input, in the order and
 * with the wording Replicate's playground uses, so moving between the two
 * needs no translating.
 *
 * Dependencies: FileField.jsx, utils/video.js
 */

import FileField from './FileField.jsx';
import { estimate, gbp, usd } from '../../utils/video.js';

function Field({ name, type, required, note, hint, children }) {
  return (
    <div className="vid-field">
      <div className="vid-field__label">
        <span className="vid-field__name">{name}</span>
        {required && <span className="vid-field__required">*</span>}
        <span className="vid-field__type">{type}</span>
        {note}
      </div>
      {children}
      {hint && <div className="vid-field__hint">{hint}</div>}
    </div>
  );
}

function Toggle({ name, checked, onChange, description, fallback }) {
  return (
    <label className="vid-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="vid-field__name">{name}</span> <span className="vid-field__type">boolean</span>
        <span className="vid-field__hint">{description}</span>
        <span className="vid-field__default">Default: {String(fallback)}</span>
      </span>
    </label>
  );
}

const Default = ({ value }) => <span className="vid-field__default">Default: {value}</span>;

export default function VideoForm({ brandId, options: o, form, onChange, onReset, onRun, running }) {
  const set = (key) => (value) => onChange((f) => ({ ...f, [key]: value }));
  const price = estimate(form, o);
  const left = o.cap.capPence - o.cap.spentPence - o.cap.inFlightPence;
  const canRun = o.configured && form.prompt.trim().length > 0 && !running && !!brandId;
  const { min, max } = o.duration;

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canRun) {
      e.preventDefault();
      onRun();
    }
  };

  return (
    <div className="vid-form" onKeyDown={onKeyDown}>
      <h2 className="vid__title">Input</h2>

      <Field name="prompt" type="string" required hint="Text prompt for video generation.">
        <textarea
          value={form.prompt}
          onChange={(e) => set('prompt')(e.target.value)}
          rows={3}
          maxLength={o.maxPromptLength}
          placeholder="The prune says “And this, kids, is how you generate a video in less than 10 seconds”."
        />
      </Field>

      <Field
        name="image"
        type="file"
        hint="Input image to generate video from (image-to-video). Supports jpg, jpeg, png, webp."
      >
        <FileField kind="picture" brandId={brandId} value={form.image} onChange={set('image')} />
      </Field>

      <Field
        name="last_frame_image"
        type="file"
        hint="Reference image for the last frame of the video. Supports jpg, jpeg, png, webp."
      >
        <FileField
          kind="picture"
          brandId={brandId}
          value={form.last_frame_image}
          onChange={set('last_frame_image')}
        />
      </Field>

      <Field name="audio" type="file" hint="Input audio to condition video generation. Supports flac, mp3, wav.">
        <FileField
          kind="audio"
          brandId={brandId}
          value={form.audio}
          onChange={set('audio')}
          accept={o.audio.types}
        />
      </Field>

      <Field
        name="duration"
        type="integer"
        note={<span className="vid-field__range">(minimum: {min}, maximum: {max})</span>}
        hint={
          <>
            Duration of the video in seconds ({min}-{max}). Ignored when audio is provided.
            <Default value={o.duration.default} />
          </>
        }
      >
        <div className="vid-duration">
          <input
            type="number"
            min={min}
            max={max}
            step={1}
            value={form.duration}
            disabled={!!form.audio}
            onChange={(e) => set('duration')(e.target.value === '' ? '' : Number(e.target.value))}
            aria-label="Duration in seconds"
          />
          <input
            type="range"
            min={min}
            max={max}
            step={1}
            value={Number(form.duration) || o.duration.default}
            disabled={!!form.audio}
            onChange={(e) => set('duration')(Number(e.target.value))}
            aria-label="Duration"
          />
        </div>
      </Field>

      <Field
        name="aspect_ratio"
        type="string"
        hint={
          <>
            Aspect ratio of the video. Ignored when an input image is provided.
            <Default value={`"${o.aspectRatios.default}"`} />
          </>
        }
      >
        <select value={form.aspect_ratio} onChange={(e) => set('aspect_ratio')(e.target.value)}>
          {o.aspectRatios.values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Field>

      <Field
        name="resolution"
        type="string"
        hint={
          <>
            Resolution of the video.
            <Default value={`"${o.resolutions.default}"`} />
          </>
        }
      >
        <select value={form.resolution} onChange={(e) => set('resolution')(e.target.value)}>
          {o.resolutions.values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Field>

      <Field
        name="fps"
        type="integer"
        hint={
          <>
            Frames per second of the video.
            <Default value={o.fps.default} />
          </>
        }
      >
        <select value={form.fps} onChange={(e) => set('fps')(Number(e.target.value))}>
          {o.fps.values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Field>

      <Toggle
        name="draft"
        checked={form.draft}
        onChange={set('draft')}
        description="Draft mode. Generates a lower-quality preview of the video."
        fallback={o.defaults.draft}
      />
      <Toggle
        name="prompt_upsampling"
        checked={form.prompt_upsampling}
        onChange={set('prompt_upsampling')}
        description="Use prompt upsampling to enhance the prompt."
        fallback={o.defaults.prompt_upsampling}
      />
      <Toggle
        name="disable_safety_filter"
        checked={form.disable_safety_filter}
        onChange={set('disable_safety_filter')}
        description="Disable safety filter for prompts (and input image). When disabled, prompts are not checked for unsafe content before generation."
        fallback={o.defaults.disable_safety_filter}
      />
      <Toggle
        name="save_audio"
        checked={form.save_audio}
        onChange={set('save_audio')}
        description="Save the video with audio."
        fallback={o.defaults.save_audio}
      />

      <Field name="seed" type="integer" hint="Random seed. Set for reproducible generation.">
        <input
          type="number"
          min={0}
          step={1}
          value={form.seed}
          placeholder="Random"
          onChange={(e) => set('seed')(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </Field>

      <div className="vid-form__foot">
        <div className="vid-price">
          <strong>≈ {gbp(price.pence)}</strong>{' '}
          <span>
            {usd(price.usd)} · {price.seconds}s
            {price.lengthKnown ? '' : ' (audio length unknown, priced at the maximum)'}
          </span>
          <span className="vid-price__cap">{gbp(left)} left of this month’s cap</span>
        </div>

        <div className="btn-row">
          <button type="button" className="btn" onClick={onReset} disabled={running}>
            Reset to default inputs
          </button>
          <button type="button" className="btn btn--primary" onClick={onRun} disabled={!canRun}>
            {running ? 'Starting…' : 'Run'} <kbd>Ctrl+Enter</kbd>
          </button>
        </div>
      </div>
    </div>
  );
}
